import { IBroadcastCommandModalBoxOption } from '@/interfaces';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { broadcastService } from '@/services';
import { useEffect, useState } from 'react';
import { useScreenSize } from '@/hooks';
import { cn } from '@/lib/utils';
import { filter } from 'rxjs';

interface IState extends IBroadcastCommandModalBoxOption {
}

const VARIABLES = {
  initState: {
    isOpen: false,
    width: 'lg'
  } as IState
};

const GenericModal = () => {

  const [state, setState] = useState<IState>(VARIABLES.initState);
  const { isMobile } = useScreenSize();

  useEffect(
    () => {
      const subscription = (
        broadcastService
          .observable()
          .pipe(filter(l => l.type === 'modal'))
          .subscribe(
            command => {
              const payload = command.payload as IBroadcastCommandModalBoxOption;

              setState(
                l => ({
                  ...l,
                  enableClickOutside: payload.enableClickOutside ?? false,
                  fullScreenOnMobile: payload.fullScreenOnMobile ?? false,
                  showCloseIcon: payload.showCloseIcon ?? true,
                  className: payload.className,
                  widthCustom: payload.widthCustom,
                  content: payload.content,
                  isOpen: payload.isOpen,
                  width: payload.width
                })
              );
            }
          )
      );

      return () => {
        subscription.unsubscribe();
      };
    },
    []
  );

  const isFullScreen = isMobile && !!state.fullScreenOnMobile;

  const getClassName = () => (
    state.width === 'xs'
      ? `md:w-[20rem]`
      : state.width === 'sm'
        ? `md:w-[24rem]`
        : state.width === 'md'
          ? `md:w-md`
          : state.width === 'lg'
            ? `md:w-lg`
            : state.width === 'xl'
              ? `md:w-xl`
              : state.width === '2xl'
                ? `md:w-2xl`
                : state.width === '3xl'
                  ? `md:w-3xl`
                  : state.width === '4xl'
                    ? `md:w-4xl`
                    : state.width === '5xl'
                      ? `md:w-5xl`
                      : state.width === '6xl'
                        ? `md:w-6xl`
                        : state.width === '7xl'
                          ? `md:w-7xl`
                          : state.width === 'full'
                            ? `md:w-full`
                            : state.width === 'custom'
                              ? `md:w-[${state.widthCustom ?? '32rem'}]`
                              : `md:w-lg`
  );

  const handleOnClickCloseIcon = () =>
    broadcastService.modal.hide();

  const handleOnOpenChange = (open: boolean) => {
    !open &&
      state.enableClickOutside &&
      handleOnClickCloseIcon();
  };

  return (
    <Dialog
      modal
      onOpenChange={handleOnOpenChange}
      open={state.isOpen}>
      <DialogContent
        showCloseIcon={state.showCloseIcon}
        onClickCloseIcon={handleOnClickCloseIcon}
        className={cn(
          'flex flex-col',
          'max-w-none',
          'w-[calc(100%-2rem)] md:w-auto',
          'rounded-2xl',
          isFullScreen
            ? 'h-screen rounded-none'
            : 'max-h-[90vh] overflow-y-auto',
          getClassName(),
          state.className
        )}>
        <VisuallyHidden.Root>
          <DialogTitle>Modal</DialogTitle>
          <DialogDescription>Description</DialogDescription>
        </VisuallyHidden.Root>
        {state.content}
      </DialogContent>
    </Dialog>
  );

};

export default GenericModal;
